/* Quote results — fires POST /api/elcapp/quote (login-gated) and walks the
 * customer through one state panel with the koala mascot: waiting → rate
 * found → saved, or no rates / custom quote / failed.
 *
 * The portal sells ELC products, not airlines: the server sends no carrier,
 * routing or aircraft (elcapp_quotes._PORTAL_HIDDEN), and the waiting screen
 * counts carrier responses without naming them (`carriers_checked`).
 *
 * Waiting is paced, not real progress: a contract rate prices instantly, a
 * live market rate (FU-44) takes ~15–30 s, and the request is one call
 * either way. The panel runs at least MIN_MS so an instant answer still reads
 * as a comparison; a slow one holds at 90% until the answer is in. */
import { useState, useEffect, useRef, C, Ic, Steps } from './common.js';

// Luke 2026-11-03: 12 s felt too quick — 18 s.
const MIN_MS       = 18000;   // shortest wait, even when the price is instant
const CHECK_MS     = 3000;    // "contract capacity" row
const RESPONSES_MS = 15000;   // carrier responses count up between CHECK_MS and this
const FINISH_MS    = 3000;    // "applying ELC pricing" before the answer shows
const LATE_MS      = 15000;   // still no answer → "waiting on airline responses"

const MASCOT = (name) => `/static/elcapp/img/mascot/${name}.png`;
const code3  = (c) => (c || '').slice(-3);
const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
const money  = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const Eyebrow = ({ accent, busy, children }) =>
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
    {busy && <span style={{ position: 'relative', width: 8, height: 8 }}>
      <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: accent }} />
      <span className="elc-ping" style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: accent }} />
    </span>}
    <span className="mono" style={{ fontSize: 11, letterSpacing: '.1em', textTransform: 'uppercase', color: accent, fontWeight: 600 }}>{children}</span>
  </div>;

// One mascot + copy panel; every state renders through it.
const StatePanel = ({ mascot, busy, eyebrow, accent, title, sub, children }) =>
  <div className="card fadein" key={mascot} style={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 32px', boxShadow: '0 1px 3px rgba(15,23,42,0.06)' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 48, maxWidth: 760, width: '100%', justifyContent: 'center', flexWrap: 'wrap' }}>
      <div style={{ position: 'relative', flexShrink: 0, width: 196, height: 210, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {busy && <div className="elc-orbit" />}
        <img className="elc-mascot" src={MASCOT(mascot)} alt="" style={{ position: 'relative' }} />
      </div>
      <div style={{ flex: 1, minWidth: 280, maxWidth: 420 }} role="status" aria-live="polite">
        <Eyebrow accent={accent} busy={busy}>{eyebrow}</Eyebrow>
        <div className="bc" style={{ fontSize: 30, fontWeight: 700, color: C.text, lineHeight: 1.1 }}>{title}</div>
        {sub && <div style={{ fontSize: 14, lineHeight: 1.55, color: C.textM, marginTop: 10 }}>{sub}</div>}
        {children}
      </div>
    </div>
  </div>;

const Summary = ({ qd, onEdit }) =>
  <div className="card" style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 22, flexWrap: 'wrap', marginBottom: 18 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <Ic.Plane sz={16} col={C.gold} r={90} />
      <span className="mono" style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{code3(qd?.org)} → {code3(qd?.dst)}</span>
    </div>
    <div style={{ width: 1, height: 26, background: C.brd }} />
    {[['Chargeable', `${qd?.chargeWt || '—'} kg`], ['Pieces', (qd?.pieces || []).reduce((n, p) => n + (parseInt(p.qty, 10) || 0), 0) || '—'],
      ['Ready', qd?.date || '—'], ['Commodity', qd?.type || 'General Cargo'],
      ...(qd?.pickupLabel ? [['Pickup', qd.pickupLabel]] : [])].map(([l, v]) =>
      <div key={l} style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <span className="mono" style={{ fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase', color: C.textD }}>{l}</span>
        <span className={l === 'Commodity' || l === 'Pickup' ? '' : 'mono'} style={{ fontSize: 13.5, color: C.text, fontWeight: l === 'Commodity' ? 600 : 400 }}>{v}</span>
      </div>)}
    <span onClick={onEdit} style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 600, color: C.blue, cursor: 'pointer' }}>Edit search</span>
  </div>;

const Row = ({ state, label, tag, done, active, wait }) =>
  <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
    <span style={{ width: 20, height: 20, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      {state === 'done' && <span style={{ width: 20, height: 20, borderRadius: '50%', background: 'rgba(5,150,105,0.12)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><Ic.Check sz={12} col={C.green} /></span>}
      {state === 'active' && <span className="elc-spinner" />}
      {state === 'pending' && <span style={{ width: 9, height: 9, borderRadius: '50%', background: C.s3 }} />}
    </span>
    <span className="mono" style={{ fontSize: 11, fontWeight: 600, color: C.textD, width: 22 }}>{tag}</span>
    <span style={{ flex: 1, fontSize: 13.5, color: C.textM }}>{label}</span>
    <span style={{ fontSize: 12, fontWeight: 500, color: state === 'done' ? C.green : state === 'active' ? C.blue : C.textD }}>
      {state === 'done' ? done : state === 'active' ? active : wait}
    </span>
  </div>;

const Btn = ({ kind = 'bb', ...p }) => <button className={`btn ${kind}`} style={{ fontSize: 13, padding: '10px 18px' }} {...p} />;

const QuoteResults = ({ setScreen, quoteData, setSQ, user, userLoaded, setPostLoginAction }) => {
  // res: { ok, status, data } once the call settles; resAt = when.
  const [run, setRun] = useState(0);
  const [res, setRes] = useState(null);
  const [now, setNow] = useState(Date.now());
  const start = useRef(Date.now());
  const resAt = useRef(0);
  const [exp, setExp] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState(null);     // { quote_ref, valid_until }

  useEffect(() => {
    if (!userLoaded || !quoteData) return;
    if (!user.loggedIn) {
      setPostLoginAction({ quoteData, intent: 'quote' });
      setScreen('login');
      return;
    }
    start.current = Date.now();
    setNow(start.current);
    setRes(null); setSaved(null); setErr(''); setExp(false);
    let live = true;
    const settle = (r) => { if (live) { resAt.current = Date.now(); setRes(r); } };
    // quoteData.org/dst are FULL lane codes (USLAX/CNCAN) from quote-lanes —
    // exactly what the pricing engine matches on.
    fetch('/api/elcapp/quote', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        origin:        quoteData.org || '',
        dest:          quoteData.dst || '',
        commodity:     quoteData.type     || 'General Cargo',
        charge_weight: quoteData.chargeWt || 100,
        ready_day:     quoteData.date     || null,
        cargo_details: quoteData.cargo_details || (Array.isArray(quoteData.pieces) ? { pieces: quoteData.pieces } : {}),
        // Pickup (priced into the total) and the extra-services request
        // (a flag staff quote by hand) — quoteform.js builds both.
        pickup:             quoteData.pickup || null,
        services_requested: !!quoteData.services_requested,
        services_note:      quoteData.services_note || '',
      })
    })
    .then(r => r.json().catch(() => ({})).then(data => settle({ ok: r.ok || r.status === 422, status: r.status, data })))
    .catch(() => settle({ ok: false, status: 0, data: {} }));
    return () => { live = false; };
  }, [user.loggedIn, userLoaded, run]);

  // A failed call answers at once; a priced / empty one finishes the paced wait.
  const finishAt = !res ? Infinity
    : !res.ok ? resAt.current
    : Math.max(start.current + MIN_MS, resAt.current + FINISH_MS);
  const done = now >= finishAt;

  useEffect(() => {
    if (done) return;
    const t = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(t);
  }, [done, run]);

  useEffect(() => {
    if (res?.status === 401) {
      setPostLoginAction({ quoteData, intent: 'quote' });
      setScreen('login');
    }
  }, [res]);

  const edit = () => setScreen('quote');
  const shell = (panel) => <div style={{ maxWidth: 960, margin: '0 auto', padding: '36px 20px' }}>
    <Steps cur={1} />
    <Summary qd={quoteData} onEdit={edit} />
    {panel}
  </div>;

  if (!userLoaded || !quoteData) return shell(null);

  // ── waiting ──────────────────────────────────────────────────────────────
  if (!done) {
    const t = now - start.current;
    const n = res?.data?.carriers_checked || 0;
    const finishing = res && now >= finishAt - FINISH_MS;
    const late = !res && t >= LATE_MS;
    const pct = Math.round(res
      ? Math.max(Math.min(90, t / MIN_MS * 100), (now - start.current) / (finishAt - start.current) * 100)
      : Math.min(90, t / MIN_MS * 100));
    // Carrier responses tick in evenly between CHECK_MS and RESPONSES_MS. The
    // count only arrives with the answer, so a slow (market) call shows one
    // generic "airline rates" row until then.
    const got = finishing ? n
      : Math.min(n, Math.max(0, Math.floor((t - CHECK_MS) / ((RESPONSES_MS - CHECK_MS) / Math.max(n, 1)))));
    const m = finishing ? { mascot: 'thinking', eyebrow: 'Optimizing', accent: C.blue, title: 'Finding your best option',
                            sub: 'Comparing every response by price and transit time, and applying ELC pricing.' }
            : late      ? { mascot: 'waiting', eyebrow: 'Live market rates', accent: C.gold, title: 'Waiting on airline responses',
                            sub: 'Live airline rates can take up to 30 seconds. Hang tight — your rate shows as soon as it is in.' }
            :             { mascot: 'processing', eyebrow: 'Checking rates', accent: C.blue, title: 'Getting your rate',
                            sub: 'We are checking contract capacity and collecting carrier responses for this lane. This usually takes 15–20 seconds.' };
    return shell(<StatePanel busy {...m}>
      <div style={{ marginTop: 22 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
          <span className="mono" style={{ fontSize: 12, color: C.textM }}>
            {n ? `Responses received ${got} / ${n}` : 'Requesting airline rates'}
          </span>
          <span className="mono" style={{ fontSize: 12, fontWeight: 600, color: m.accent }}>{pct}%</span>
        </div>
        <div style={{ height: 7, borderRadius: 999, background: C.s1, overflow: 'hidden' }}>
          <div className="elc-bar" style={{ width: `${pct}%` }} />
        </div>
        {/* Three real stages. No per-airline rows: without names (the portal
            never shows them) a numbered list of "partners" reads as staged. */}
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 9 }}>
          <Row tag="01" label="Contract capacity" state={t >= CHECK_MS ? 'done' : 'active'} done="Checked" active="Checking…" />
          <Row tag="02" label="Live airline rates" state={finishing ? 'done' : t >= CHECK_MS ? 'active' : 'pending'}
               done={n ? `${n} of ${n} responses received` : 'Received'}
               active={n ? `${got} of ${n} responses received…` : 'Requesting…'} wait="Waiting" />
          <Row tag="03" label="ELC pricing" state={finishing ? 'active' : 'pending'} active="Applying…" wait="Waiting" />
        </div>
        <div style={{ marginTop: 24 }}><Btn kind="bo" onClick={edit}>Cancel</Btn></div>
      </div>
    </StatePanel>);
  }

  const d = res.data || {};
  const q = (d.quotes || [])[0];

  // ── failed ───────────────────────────────────────────────────────────────
  if (!res.ok) return shell(<StatePanel mascot="sorry" eyebrow="Request failed" accent={C.red}
      title="We couldn't get your rate"
      sub="Something went wrong on our side. This is usually temporary — please try again in a moment.">
    <div style={{ marginTop: 26, display: 'flex', gap: 10 }}>
      <Btn onClick={() => setRun(run + 1)}>Try again</Btn>
      <Btn kind="bo" onClick={edit}>Modify search</Btn>
    </div>
  </StatePanel>);

  // ── over the size / weight our standard rates cover ──────────────────────
  if (!q && d.oversize) return shell(<StatePanel mascot="warning" eyebrow="Custom quote" accent={C.gold}
      title="This shipment needs a custom quote"
      sub="One or more pieces are larger or heavier than our standard rates cover. Contact ELC and we will quote it for you.">
    <div style={{ marginTop: 16, fontSize: 12.5, color: C.textM, background: 'rgba(217,119,6,0.06)', border: '1px solid rgba(217,119,6,0.25)', borderRadius: 8, padding: '10px 14px' }}>
      {(d.oversize.lines || []).map((l, i) => <div key={i}>{l}</div>)}
    </div>
    <div style={{ marginTop: 26 }}><Btn kind="bo" onClick={edit}>Modify search</Btn></div>
  </StatePanel>);

  // ── no rate ──────────────────────────────────────────────────────────────
  if (!q) return shell(<StatePanel mascot="confused" eyebrow="No results" accent={C.textM}
      title="No rates for this lane yet"
      sub={`We don't have a published rate for ${code3(quoteData.org)} → ${code3(quoteData.dst)} right now. Contact ELC for a custom quote, or try a different route.`}>
    <div style={{ marginTop: 26 }}><Btn onClick={edit}>Modify search</Btn></div>
  </StatePanel>);

  const validText = (iso) => iso ? `Valid until ${fmtDate(iso)}` : `Valid ${q.validity} days`;
  const cb0 = q.customer_breakdown || {};
  const pickupLine = (cb0.sell_surcharge_lines || []).find(l => l.kind === 'trucking');
  const Offer = ({ tone }) =>
    <div style={{ marginTop: 20, border: `1px solid ${tone === 'saved' ? 'rgba(217,119,6,0.3)' : C.brd}`, background: tone === 'saved' ? 'rgba(217,119,6,0.05)' : '#fff', borderRadius: 10, padding: '15px 17px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <div className="badge b-bl" style={{ marginBottom: 6 }}>{q.product}</div>
          <div className="mono" style={{ fontSize: 11.5, color: C.textM }}>{code3(q.origin)} → {code3(q.dest)} · {q.charge_weight} kg</div>
          <div style={{ fontSize: 12, color: C.textM, marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}><Ic.Clock sz={12} />{q.transit} transit</div>
          {pickupLine && <div style={{ fontSize: 12, color: C.textM, marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}><Ic.Pin sz={12} />Pickup from {quoteData.pickupLabel || 'your address'} included</div>}
          {cb0.trucking_note && <div style={{ fontSize: 12, color: '#b45309', marginTop: 3 }}>{cb0.trucking_note}</div>}
          {quoteData.services_requested && <div style={{ fontSize: 12, color: '#b45309', marginTop: 3 }}>Extra services requested — our team will quote them separately.</div>}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="bc" style={{ fontSize: 32, fontWeight: 800, color: C.text, lineHeight: 1 }}>{money(q.price)}</div>
          <div style={{ fontSize: 10, color: C.textD, marginTop: 4 }}>Total, all-in</div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${tone === 'saved' ? 'rgba(217,119,6,0.3)' : C.brd}`, fontSize: 12, color: tone === 'saved' ? '#b45309' : C.textM }}>
        <Ic.Clock sz={13} />
        <span style={{ flex: 1 }}>{tone === 'saved' ? `${saved.quote_ref} · ${validText(saved.valid_until)}` : validText(q.valid_until)}</span>
        {tone !== 'saved' && <span onClick={() => setExp(!exp)} style={{ color: C.blue, fontWeight: 600, cursor: 'pointer' }}>{exp ? 'Hide breakdown' : 'Price breakdown'}</span>}
      </div>
      {/* The customer's OWN price itemization (sell side, FU-4). */}
      {exp && tone !== 'saved' && (() => {
        const cb = q.customer_breakdown || {};
        return <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0' }}>
            <span style={{ color: C.textM }}>Air Freight{cb.sell_rate_per_kg ? ` · $${Number(cb.sell_rate_per_kg).toFixed(2)}/kg × ${q.charge_weight} kg` : ''}</span>
            <span className="mono">${Number(cb.sell_freight || 0).toFixed(2)}</span>
          </div>
          {(cb.sell_surcharge_lines || []).map((l, i) =>
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0' }}>
              <span style={{ color: C.textM }}>{l.label}{l.display ? ` · ${l.display}` : ''}</span>
              <span className="mono">${Number(l.amount || 0).toFixed(2)}</span>
            </div>)}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, borderTop: `1px solid ${C.brd}`, paddingTop: 6, marginTop: 4 }}>
            <span>Total</span><span className="mono" style={{ color: C.green }}>${Number(cb.sell_total || q.price || 0).toFixed(2)}</span>
          </div>
        </div>;
      })()}
    </div>;

  // ── saved ────────────────────────────────────────────────────────────────
  if (saved) return shell(<StatePanel mascot="celebrating" eyebrow="Quote saved" accent={C.gold}
      title="Your rate is saved"
      sub={`We hold this price until ${saved.valid_until ? fmtDate(saved.valid_until) : 'it expires'}. ${user.bookingEnabled ? 'Book it from My Quotes whenever you are ready.' : 'Find it any time under My Quotes.'}`}>
    <Offer tone="saved" />
    <div style={{ marginTop: 26, display: 'flex', gap: 10 }}>
      <Btn onClick={() => setScreen('myquotes')}>View my quotes</Btn>
      <Btn kind="bo" onClick={edit}>New quote</Btn>
    </div>
  </StatePanel>);

  // The server rebuilds the saved row from search_ref (re-priced, audited);
  // the price rides along purely as a drift check.
  const accept = async (intent) => {
    setErr(''); setAccepting(true);
    try {
      const r = await fetch('/api/elcapp/quotes/accept', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ search_ref: d.search_ref, price: q.price })
      }).then(x => x.json());
      setAccepting(false);
      if (!r.quote_ref) { setErr(r.error || 'Failed to save quote. Please try again.'); return; }
      if (intent === 'save') setSaved({ quote_ref: r.quote_ref, valid_until: r.valid_until });
      else { setSQ({ ...q, quote_ref: r.quote_ref }); setScreen('booking'); }
    } catch {
      setAccepting(false);
      setErr('Connection error. Please try again.');
    }
  };

  // ── rate found ───────────────────────────────────────────────────────────
  return shell(<StatePanel mascot="happy" eyebrow="Rate found" accent={C.blue}
      title={`Your ${q.product} rate is ready`}
      sub="Save it to hold this price, or book it straight away.">
    <Offer />
    {err && <div style={{ marginTop: 12, background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 8, padding: '9px 14px', fontSize: 13, color: C.red }}>{err}</div>}
    <div style={{ marginTop: 22, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
      <Btn disabled={accepting} onClick={() => accept('save')}>{accepting ? 'Saving…' : 'Save quote'}</Btn>
      <Btn kind="bo" disabled={accepting || !user.bookingEnabled}
           title={user.bookingEnabled ? undefined : 'Booking is not yet available — save your quote and we’ll be in touch.'}
           onClick={() => accept('book')}>Accept & book</Btn>
      <Btn kind="bo" onClick={edit}>Modify search</Btn>
    </div>
  </StatePanel>);
};

export { QuoteResults };
